import { useScrollReveal } from '@/hooks/useScrollReveal';

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

interface CaseStudy {
  label: string;
  title: string;
  badges: string[];
  whatWeDid: string;
  outcomes: string[];
  channel: string;
  cardBg: string;
}

const caseStudies: CaseStudy[] = [
  {
    label: 'ENTERPRISE · MANAGED SERVICES · INDIA / UAE / GLOBAL',
    title: 'Microsoft Managed Services - Multi-Client Programme',
    badges: ['M1 · Modernize', 'M3 · Manage Success'],
    whatWeDid: 'End-to-end Microsoft managed services across enterprise and SMB clients in BFSI, retail, public-sector across India, UAE, Africa. Discovery, assessment, infrastructure, security, Azure management, cost optimization, modern workspace, PowerApps, firewall work.',
    outcomes: [
      'Multiple domains migrated across portfolio',
      'Licensing and cost optimisation delivered',
      'BFSI app hosting - production and BCP live',
      'MDM solutions deployed and managed',
      'Adoption and support model operational',
      'PowerApps implementations live',
    ],
    channel: 'Direct Azosis delivery - enterprise and SMB',
    cardBg: 'bg-azosis-dark',
  },
  {
    label: 'ENTERPRISE · CHANNEL · MULTI-GEO',
    title: 'Multi-Geo VDI & DevOps Migration',
    badges: ['M1 · Modernize', 'M2 · Mobilize & Magnify', 'M3 · Manage Success'],
    whatWeDid: 'Global technology enterprise via channel partner. VDI, DevOps secured coding, licensing across 3 geographies. 300 use cases pre-workshop. Two-day decision workshop. CTO stakeholder. 8 laptops to 1 per user. Expanded into full digital transformation.',
    outcomes: [
      '7,000+ users live on programme',
      '300 use cases tested - zero surprises',
      '8 laptops to 1 per user',
      'Licensing optimisation delivered',
      'Secured coding and DevOps live',
      'Unstated transformation requirements delivered',
      'Three-geo Agile rollout completed',
    ],
    channel: 'Global tech manufacturer/distributor (identity withheld)',
    cardBg: 'bg-azosis-surface',
  },
  {
    label: 'M&A · TENANT MIGRATION · ENTERPRISE',
    title: 'GSuite to O365 + Hybrid Model Consolidation',
    badges: ['M1 · Modernize', 'M2 · Mobilize & Magnify'],
    whatWeDid: 'Post-M&A healthcare tech company. Two GSuite entities into one O365 tenant via cloud reseller. Identity, mail, data, VM migration, hybrid DaaS. All scenarios trailed before execution.',
    outcomes: [
      'Two entities into one O365 tenant',
      'Full SSO across acquired companies',
      'Data migrated - zero security compromise',
      'VMs and hybrid model live',
      'VDI and apps integrated',
      'All CTO queries resolved via advisory',
    ],
    channel: 'Global cloud services reseller (identity withheld)',
    cardBg: 'bg-azosis-dark',
  },
  {
    label: 'TELCO · AFRICA',
    title: 'Analytics Assessment & Cloud Roadmap',
    badges: ['M1 · Modernize', 'M3 · Manage Success'],
    whatWeDid: "Africa's leading mobile operator via Microsoft channel partner. Data and analytics assessment Apr–Jun 2023. Validated tech environment, identified challenges, delivered roadmap - discovery, LLD, cost analysis, managed services for IT and Marketing.",
    outcomes: [
      'Full current state validated - IT and Marketing',
      'Challenges documented and prioritised',
      'Roadmap delivered in 8-week engagement',
      'Up to 60% cost reduction identified',
      'Managed services scoped',
      'Cloud migration path delivered',
    ],
    channel: 'Microsoft assessment programme - channel partner',
    cardBg: 'bg-azosis-surface',
  },
];

const heroStats = [
  { value: '30+', label: 'Customers' },
  { value: '65%', label: 'Cost reduction' },
  { value: '7,000+', label: 'Users' },
  { value: '300+', label: 'Use cases pre-built' },
];

export default function CaseStudies() {
  return (
    <div className="bg-azosis-dark min-h-screen pt-20">
      {/* S1 — HERO */}
      <section className="py-16 md:py-24 bg-azosis-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-azosis-gold/15 rounded-full blur-[150px]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-azosis-bronze/10 rounded-full blur-[120px]"></div>
        </div>

        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
          <div className="max-w-5xl mx-auto text-center">
            <SectionReveal>
              <span className="text-azosis-gold text-xs tracking-[0.2em] uppercase mb-4 block">
                Azosis Case Studies
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-[1.15]">
                <span className="text-white">Real outcomes.</span>{' '}
                <span style={{ color: '#C9A96E' }}>Real engagements.</span>
              </h1>
              <p className="text-azosis-textSecondary text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-12">
                Selected results across geographies and industries - from migrations to managed services.
              </p>
            </SectionReveal>

            {/* Stats */}
            <SectionReveal delay={150}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
                {heroStats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-azosis-gold mb-2">
                      {stat.value}
                    </div>
                    <div className="text-azosis-textMuted text-xs md:text-sm tracking-wide">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* S2–S5 — Case Study Cards */}
      {caseStudies.map((cs, index) => (
        <section key={index} className={`py-12 md:py-16 ${cs.cardBg}`}>
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-6xl mx-auto">
              <SectionReveal>
                <div className={`border border-azosis-border ${cs.cardBg === 'bg-azosis-surface' ? 'bg-azosis-dark' : 'bg-azosis-surface'}`}>
                  {/* TOP — Label + Title + Badges */}
                  <div className="p-6 md:p-8 lg:p-10 border-b border-azosis-border text-center">
                    <span className="text-azosis-gold text-xs tracking-[0.2em] uppercase mb-3 block">
                      {cs.label}
                    </span>
                    <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 leading-[1.2]">
                      {cs.title}
                    </h2>
                    <div className="flex flex-wrap gap-3 justify-center">
                      {cs.badges.map((badge, bi) => (
                        <span
                          key={bi}
                          className="px-4 py-1.5 bg-azosis-gold/10 border border-azosis-gold/30 text-azosis-gold text-xs font-semibold tracking-wide"
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* BOTTOM — Two Column: What We Did | Outcomes + Channel */}
                  <div className="p-6 md:p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                    {/* LEFT — What We Did */}
                    <div>
                      <h3 className="text-white text-sm font-semibold tracking-[0.15em] uppercase mb-3">
                        What we did
                      </h3>
                      <p className="text-azosis-textSecondary text-sm md:text-base leading-relaxed">
                        {cs.whatWeDid}
                      </p>
                    </div>

                    {/* RIGHT — Outcomes + Channel */}
                    <div>
                      <h3 className="text-white text-sm font-semibold tracking-[0.15em] uppercase mb-3">
                        Outcomes
                      </h3>
                      <ul className="space-y-2 mb-5">
                        {cs.outcomes.map((outcome, oi) => (
                          <li key={oi} className="flex items-start gap-3">
                            <i className="ri-check-line text-azosis-gold text-sm flex-shrink-0 mt-0.5"></i>
                            <span className="text-azosis-textSecondary text-sm md:text-base leading-relaxed">
                              {outcome}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* Channel Note */}
                      <div className="pt-4 border-t border-azosis-border">
                        <span className="text-azosis-textMuted text-xs tracking-wide uppercase block mb-1">
                          Channel
                        </span>
                        <p className="text-azosis-textSecondary text-sm italic">
                          {cs.channel}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </SectionReveal>
            </div>
          </div>
        </section>
      ))}

      {/* S6 — CTA */}
      <section className="py-8 md:py-12 bg-azosis-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-azosis-gold/20 rounded-full blur-[150px]"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-azosis-bronze/15 rounded-full blur-[120px]"></div>
        </div>

        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
          <div className="max-w-3xl mx-auto text-center">
            <SectionReveal>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                Ready to be the next case study?
              </h2>
              <p className="text-azosis-textSecondary text-base md:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
                Every Azosis engagement begins with a conversation. We listen first, document the scope alongside your team, and produce evidence - fast - that the problem is solvable and the path is clear.
              </p>
              <div className="flex items-center justify-center">
                <a
                  href="https://www.azosis.com/gc/contact.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
                >
                  Start a conversation
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>
    </div>
  );
}